import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  partnerLogin,
  getPartnerProfile,
} from '../api/partnerPortal';
import { setPartnerAuthToken } from '../api/partnerClient';

const STORAGE_KEY = 'studioSivadPartnerAuth';

const PartnerAuthContext = createContext({
  session: null,
  partner: null,
  partnerUser: null,
  token: null,
  initializing: true,
  authenticating: false,
  login: async () => {},
  logout: () => {},
  refreshProfile: async () => {},
  syncProfile: async () => {},
  error: null,
});

export const PartnerAuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [token, setToken] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.token) {
          setToken(parsed.token);
          const restoredSession =
            parsed.session ||
            (parsed.partner ? { partner: parsed.partner } : null);
          setSession(restoredSession);
          setPartnerAuthToken(parsed.token);
        }
      }
    } catch (storageError) {
      console.warn('Failed to restore partner auth session', storageError);
    } finally {
      setInitializing(false);
    }
  }, []);

  const persistSession = useCallback((nextToken, nextSession) => {
    setToken(nextToken);
    setSession(nextSession);
    setPartnerAuthToken(nextToken);

    if (nextToken && nextSession) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          token: nextToken,
          session: nextSession,
        })
      );
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const syncProfile = useCallback(
    (profile, baseSession = null, sessionToken = null) => {
      const activeToken = sessionToken || token;
      if (!activeToken) return;

      const sourceSession = baseSession || session || {};
      const updatedSession = {
        ...sourceSession,
        partner: {
          ...(sourceSession.partner || {}),
          profile,
        },
      };

      setSession(updatedSession);
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          token: activeToken,
          session: updatedSession,
        })
      );
    },
    [session, token]
  );

  const login = useCallback(
    async ({ email, password }) => {
      setAuthenticating(true);
      setError(null);
      try {
        const data = await partnerLogin({ email, password });
        const baseSession = {
          partner: data.partner,
          user: data.user,
        };

        persistSession(data.token, baseSession);

        try {
          const profile = await getPartnerProfile();
          syncProfile(profile, baseSession, data.token);
          return {
            ...data,
            partner: {
              ...data.partner,
              profile,
            },
            profile,
          };
        } catch (profileError) {
          console.warn('Partner login succeeded but profile fetch failed', profileError);
          return data;
        }
      } catch (loginError) {
        setError(loginError);
        throw loginError;
      } finally {
        setAuthenticating(false);
      }
    },
    [persistSession, syncProfile]
  );

  const logout = useCallback(() => {
    persistSession(null, null);
  }, [persistSession]);

  const refreshProfile = useCallback(async () => {
    if (!token) return null;
    try {
      const profile = await getPartnerProfile();
      syncProfile(profile);
      return profile;
    } catch (profileError) {
      setError(profileError);
      throw profileError;
    }
  }, [syncProfile, token]);

  useEffect(() => {
    if (!token) {
      setPartnerAuthToken(null);
    }
  }, [token]);

  const contextValue = useMemo(
    () => ({
      session,
      partner: session?.partner || null,
      partnerUser: session?.user || null,
      token,
      initializing,
      authenticating,
      login,
      logout,
      refreshProfile,
      syncProfile,
      error,
    }),
    [
      session,
      token,
      initializing,
      authenticating,
      login,
      logout,
      refreshProfile,
      syncProfile,
      error,
    ]
  );

  return (
    <PartnerAuthContext.Provider value={contextValue}>
      {children}
    </PartnerAuthContext.Provider>
  );
};

export const usePartnerAuth = () => useContext(PartnerAuthContext);
